import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from '@/hooks/useIsMobile';

interface TooltipFFXIVProps {
    children: React.ReactNode;
    className?: string;
    position?: 'left' | 'right';
    usePortal?: boolean;
    showInMobile?: boolean;
}

const TooltipFFXIV = ({ children, className, position = 'right', usePortal = true, showInMobile = true }: TooltipFFXIVProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const contentRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const lastMousePosition = useRef({ x: 0, y: 0 });
    const isMobile = useIsMobile();

    // Track global mouse position
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            lastMousePosition.current = { x: e.clientX, y: e.clientY };
        };
        document.addEventListener('mousemove', handleMouseMove);
        return () => document.removeEventListener('mousemove', handleMouseMove);
    }, []);



    useEffect(() => {
        if (triggerRef.current) {
            const parent = triggerRef.current.parentElement;
            if (parent) {
                const handleMouseEnter = () => {
                    if (!isMobile) setIsVisible(true);
                };
                const handleMouseLeave = () => setIsVisible(false);
                const handleClick = () => {
                    if (isMobile) {
                        setIsModalOpen(true);
                    }
                };
                
                parent.addEventListener('pointerenter', handleMouseEnter);
                parent.addEventListener('pointerleave', handleMouseLeave);
                parent.addEventListener('mouseleave', handleMouseLeave);
                parent.addEventListener('click', handleClick);
                return () => {
                    parent.removeEventListener('pointerenter', handleMouseEnter);
                    parent.removeEventListener('pointerleave', handleMouseLeave);
                    parent.removeEventListener('mouseleave', handleMouseLeave);
                    parent.removeEventListener('click', handleClick);
                };
            }
        }
    }, [isMobile]);

    // Periodic check to ensure tooltip hides if mouse left without triggering event
    useEffect(() => {
        if (!isVisible) return;

        const checkMousePosition = () => {
            if (triggerRef.current) {
                const parent = triggerRef.current.parentElement;
                if (parent) {
                    const rect = parent.getBoundingClientRect();
                    const { x, y } = lastMousePosition.current;
                    const isOutside = 
                        x < rect.left || 
                        x > rect.right || 
                        y < rect.top || 
                        y > rect.bottom;
                    
                    if (isOutside) {
                        setIsVisible(false);
                    }
                }
            }
        };

        const intervalId = setInterval(checkMousePosition, 150);
        return () => clearInterval(intervalId);
    }, [isVisible]);

    useEffect(() => {
        const updatePosition = () => {
            if (triggerRef.current && isVisible) {
                const rect = triggerRef.current.getBoundingClientRect();
                const top = rect.top;
                const left = position === 'left' ? rect.left : rect.right;
                setTooltipPosition({ top, left });
            }
        };

        updatePosition();

        if (isVisible) {
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
        }

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isVisible, position]);


    const widthClass = 'max-w-[300px] w-max';
    const pyClass = 'py-2';
    const contentClass = '';
    const translateClass = position === 'left' ? '-translate-x-full' : '';

    const visibilityClass = usePortal 
        ? (isVisible ? 'opacity-100' : 'opacity-0')
        : 'opacity-0 group-hover:opacity-100';

    // Modal for mobile
    const tooltipModal = isMobile && isModalOpen && (
        <div
            className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur z-[100000] flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
        >
            <div 
                className="relative bg-[#323232] rounded-xl p-3 max-w-sm w-full mx-4 border-2 border-[#CDCCA8] shadow-[0px_0px_4px_3px_rgba(0,0,0,0.3)]"
                style={{ backgroundImage: 'url(/grid_pattern.png)', backgroundRepeat: 'repeat', backgroundSize: '5px 5px' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Top gradient overlay */}
                <div className="absolute bg-gradient-to-b from-white to-transparent h-5 w-full top-0 left-0 pointer-events-none opacity-20 rounded-t-[10px]"></div>

                <div className="text-white text-shadow-border shadow-black break-words">
                    {children}
                </div>
            </div>
        </div>
    );

    const tooltipContent = (
        <div
            className={`${usePortal ? 'fixed' : 'absolute'} px-2 ${pyClass} ${className} text-white text-shadow-border shadow-black z-[100000] pointer-events-none ${widthClass} flex items-center justify-center transition-opacity duration-100 ${visibilityClass} ${translateClass}`}
            style={usePortal ? {
                top: tooltipPosition.top,
                left: tooltipPosition.left,
            } : {
                top: 0,
                [position === 'left' ? 'right' : 'left']: '100%',
            }}
        >
            <div className="px-2 w-full h-full rounded-lg absolute bg-black blur-[1px] opacity-80 z-[-1]"></div>
            <div className={contentClass + ' break-words'} ref={contentRef}>{children}</div>
        </div>
    );

    return (
        <>
            <div
                ref={triggerRef}
                className="absolute inset-0 w-full h-full pointer-events-none"
            />
            {isMobile && showInMobile ? (
                tooltipModal && typeof document !== 'undefined' && createPortal(tooltipModal, document.body)
            ) : (
                usePortal
                    ? (typeof document !== 'undefined' && createPortal(tooltipContent, document.body))
                    : tooltipContent
            )}
        </>
    );
};

export default TooltipFFXIV;